import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import get from 'lodash/get';
import NamedBox from '../atoms/NamedBox';
import Ranks, { RanksType } from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import Accordion from '../atoms/Accordion';
import { RanksComparisonGroup } from '../molecules/RanksTooltip';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { inchesToFeetAndInches, secondsToMinutesSecondsMs } from '../services/converter';
import { FormatType, getEventFormatType, getEventName } from '../services/event-names-and-formats';
import Position from '../types/Position';
import Player from '../types/Player';
import Team from '../types/Team';
import PlayerCombineEvent from '../types/PlayerCombineEvent';
import PlayerCombineStats from '../types/PlayerCombineStats';
import PlayerNFLCombineEvent from '../types/PlayerNFLCombineEvent';
import PlayerNFLCombineStats from '../types/PlayerNFLCombineStats';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
interface PlayerProCombineDataProps {
  className?: string;
  loading?: boolean;
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  nflCombineEvents: Array<PlayerCombineEvent | PlayerNFLCombineEvent>;
  hideHeader?: boolean;
  isNFL?: boolean;
}

const MIN_EVENT_YEAR = 2010;

const useStyles = makeStyles((theme:Theme) => ({
  playerProCombineData: {
    borderBottom: 0,
    position: 'relative',
  },

  header: {
    marginBottom: theme.spacing(2),
  },

  event: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  eventLogo: {
    width: '64px',
    height: '64px',
    marginRight: theme.spacing(2),
  },
  eventInfo: {
    flexGrow: 1,
    border: 0,
    fontFamily: FONT_PROXIMA_NOVA,
  },
  eventInfoRow: {},
  eventInfoName: {
    width: '54px',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
  },
  eventInfoValue: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
    fontWeight: 'bold',
  },
  hideHeader: {
    display: 'none',
  }
}), { name: PlayerProCombineData.name });

export default function PlayerProCombineData (props:PlayerProCombineDataProps) {
  const {
    className,
    loading = false,
    player,
    collegeTeam,
    nflTeam,
    position,
    nflCombineEvents,
    hideHeader = false,
    isNFL,
  } = props;
  const classes = useStyles();

  const college = (player?.playerColleges || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = (player?.highSchools || [])
    .find((highSchool:HighSchool) => !!highSchool?.isPrimary);
  const recruitingClass = college?.recruitingClass || highSchool?.graduatingClass;

  function displayPerformance (performance:number, formatType:FormatType):string {
    if (formatType === FormatType.FEET_AND_INCHES) {
      return inchesToFeetAndInches(performance);
    } else if (formatType === FormatType.INCHES) {
      return `${Number(performance).toFixed(2)}"`;
    } else if (formatType === FormatType.MINUTES_SECONDS_MS) {
      return secondsToMinutesSecondsMs(performance);
    }

    return Number(performance).toFixed(2);
  }

  function getRanksEvent (stats:PlayerCombineStats | PlayerNFLCombineStats) {
    return {
      type: getEventName(stats.event),
      name: stats.meet ? `${stats.meet} ${stats.year ? `(${stats.year})` : ''}` : '',
      result: displayPerformance(stats.performance, getEventFormatType(stats.event)),
    };
  }

  function getEventYear (eventDate:string):number {
    return new Date(eventDate).getUTCFullYear();
  }

  function renderNFLEvents (
    stats:PlayerNFLCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.PRO_COMBINE}
        playerId={player?.id}
        teamId={nflTeam?.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        event={getRanksEvent(stats)}
        values={[
          {
            percentile: Math.round(get(stats, 'collegeTeamStats.percentile', 0)),
            value: `${get(stats, 'collegeTeamStats.playerRank', 0)}/${get(stats, 'collegeTeamStats.playerCount', 1)}`,
          },
          {
            percentile: Math.round(get(stats, 'collegeConferenceStats.percentile', 0)),
            value: `${get(stats, 'collegeConferenceStats.playerRank', 0)}/${get(stats, 'collegeConferenceStats.playerCount', 1)}`,
          },
          {
            percentile: Math.round(get(stats, 'nflTeamStats.percentile', 0)),
            value: `${get(stats, 'nflTeamStats.playerRank', 0)}/${get(stats, 'nflTeamStats.playerCount', 1)}`,
            comparisonGroup: RanksComparisonGroup.NFL_TEAM,
          },
          {
            percentile: Math.round(get(stats, 'nflUDFAStats.percentile', 0)),
            value: `${get(stats, 'nflUDFAStats.playerRank', 0)}/${get(stats, 'nflUDFAStats.playerCount', 1)}`,
            comparisonGroup: RanksComparisonGroup.NFL_UDFA,
          },
          {
            percentile: Math.round(get(stats, 'nflDraftStats.percentile', 0)),
            value: `${get(stats, 'nflDraftStats.playerRank', 0)}/${get(stats, 'nflDraftStats.playerCount', 1)}`,
            comparisonGroup: RanksComparisonGroup.NFL_DRAFT,
          },
        ]}
        bottomSpace
      />
    );
  }

  function renderCollegeEvents (
    stats:PlayerCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.COMBINE}
        playerId={player && player.id}
        teamId={collegeTeam && collegeTeam.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        event={getRanksEvent(stats)}
        values={[
          {
            percentile: Math.round(get(stats, 'hsClassStats.percentile', 0)),
            value: `${get(stats, 'hsClassStats.playerRank', 0)}/${get(stats, 'hsClassStats.playerCount', 0)}`,
          },
          {
            percentile: Math.round(get(stats, 'div1Stats.percentile', 0)),
            value: `${get(stats, 'div1Stats.playerRank', 0)}/${get(stats, 'div1Stats.playerCount', 0)}`,
          },
          {
            percentile: Math.round(get(stats, 'teamStats.percentile', 0)),
            value: `${get(stats, 'teamStats.playerRank', 0)}/${get(stats, 'teamStats.playerCount', 0)}`,
            comparisonGroup: RanksComparisonGroup.COLLEGE_TEAM,
          },
          {
            percentile: Math.round(get(stats, 'conferenceStats.percentile', 0)),
            value: `${get(stats, 'conferenceStats.playerRank', 0)}/${get(stats, 'conferenceStats.playerCount', 0)}`,
            comparisonGroup: RanksComparisonGroup.COLLEGE_CONFERENCE,
          },
          {
            percentile: Math.round(get(stats, 'nflDraftStats.percentile', 0)),
            value: `${get(stats, 'nflDraftStats.playerRank', 0)}/${get(stats, 'nflDraftStats.playerCount', 0)}`,
            comparisonGroup: RanksComparisonGroup.NFL_DRAFT,
          },
        ]}
        bottomSpace
      />
    );
  }

  function renderEvent(event:PlayerCombineEvent | PlayerNFLCombineEvent) {
    const eventYear = event?.combineDate ? new Date(event.combineDate).getUTCFullYear() : 0;

    return (
      <div className={classes.event}>
        {(event.type?.toLowerCase().includes('nfl') && event.type?.toLowerCase().includes('combine')) && (
          <img
            className={classes.eventLogo}
            src='/images/nfl-combine.png'
            alt='NFL Scouting Combine'
          />
        )}

        <table className={classes.eventInfo}>
          <tbody>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Event</td>
              <td className={classes.eventInfoValue}>{event.type}</td>
            </tr>

            {!!event.city && (
              <tr className={classes.eventInfoRow}>
                <td className={classes.eventInfoName}>City</td>
                <td className={classes.eventInfoValue}>{event.city}</td>
              </tr>
            )}

            {eventYear >= MIN_EVENT_YEAR && (
              <tr className={classes.eventInfoRow}>
                <td className={classes.eventInfoName}>Year</td>
                <td className={classes.eventInfoValue}>{getEventYear(event.combineDate)}</td>
              </tr>
            )}

            {!!event.height && (
              <tr className={classes.eventInfoRow}>
                <td className={classes.eventInfoName}>Height</td>
                <td className={classes.eventInfoValue}>{inchesToFeetAndInches(event.height, true)}</td>
              </tr>
            )}

            {!!event.weight && (
              <tr className={classes.eventInfoRow}>
                <td className={classes.eventInfoName}>Weight</td>
                <td className={classes.eventInfoValue}>{event.weight} lbs.</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* <table className={classes.eventInfo}>
          <tbody>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Hand</td>
              <td className={classes.eventInfoValue}>{9.125}</td>
            </tr>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Arm</td>
              <td className={classes.eventInfoValue}>{30.625}</td>
            </tr>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Wing</td>
              <td className={classes.eventInfoValue}>{75.875}</td>
            </tr>
          </tbody>
        </table> */}
      </div>
    );
  }

  return (
    <NamedBox
      className={clsx(classes.playerProCombineData, className)}
      headerClassName={clsx(classes.header, hideHeader && classes.hideHeader)}
      name={`Pro Combine Data - ${position}`}
    >
      <Loader inProgress={loading} />

      {!!nflCombineEvents.length && (
        nflCombineEvents.map((event:PlayerCombineEvent | PlayerNFLCombineEvent, index:number) => (
          <Accordion
            key={index}
            summary={event.type}
            expanded={index === 0 || index === 1}
          >
            {renderEvent(event)}

            {!isNFL && (
              <Ranks
                headerItems={[
                  { title: 'HS Class', subtitle: `(${recruitingClass})` },
                  { title: 'Division-I', subtitle: '(Since 2010)' },
                  { logo: collegeTeam?.logo247 || collegeTeam?.logoESPN || collegeTeam?.logoAlt },
                  { logo: get(collegeTeam, 'conference.iconUrl', '') },
                  { logo: '/images/nfl-draft.png' },
                ]}
              />
            )}

            {isNFL && (
              <Ranks
                headerItems={[
                  {
                    logo: collegeTeam?.logo247 || collegeTeam?.logoESPN || collegeTeam?.logoAlt,
                    subtitle: '(Since 2010)',
                  },
                  {
                    logo: get(collegeTeam, 'conference.iconUrl', ''),
                    subtitle: '(Since 2010)',
                  },
                  { logo: nflTeam?.logo247 || nflTeam?.logoESPN || nflTeam?.logoAlt },
                  { title: 'NFL', subtitle: 'UDFA' },
                  { logo: '/images/nfl-draft.png' },
                ]}
              />
            )}

            {(event.eventStats && !!event.eventStats.length) && (
              (event.eventStats as any[])
                .filter((stats:any) => !!stats.performance)
                .map((stats:any, index:number) => 
                (
                  isNFL
                    ? renderNFLEvents(stats as PlayerNFLCombineStats, event, index)
                    : renderCollegeEvents(stats as PlayerCombineStats, event, index)
                  )
                )
            )}
          </Accordion>
        ))
      )}
    </NamedBox>
  );
}
